import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { GOAL_STATUS_OPTIONS, stripHtml } from "../utils/goals";
import { hasNavWrite, isPrivilegedNavRole } from "../utils/navPermissions";

export default function GoalCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", status: "not_started", due_date: "" });
  const employeeId = searchParams.get("employee_id") || (user?.id || user?._id);

  const canMutateGoals =
    isPrivilegedNavRole(user?.role) || hasNavWrite(user, "/goals");

  useEffect(() => {
    if (!user) return;
    if (!canMutateGoals) {
      toast.info("You have read-only access to Goals.");
      navigate("/goals", { replace: true });
    }
  }, [user, canMutateGoals, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Goal is required");
      return;
    }
    if (!stripHtml(form.description || "")) {
      toast.error("Key Results / Measure of Success is required");
      return;
    }
    if (!form.status) {
      toast.error("Status is required");
      return;
    }
    if (!form.due_date) {
      toast.error("Due date is required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/goals", { ...form, employee_id: employeeId || undefined });
      toast.success("Goal created");
      navigate("/goals");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create goal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold text-primary mb-6">Create Goal</h1>
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Goal *</label>
          <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" placeholder="Goal" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Key Results / Measure of Success *</label>
          <div className="rounded-lg border border-gray-200 overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary">
            <ReactQuill
              theme="snow"
              value={form.description}
              onChange={(value) => setForm((f) => ({ ...f, description: value }))}
              placeholder="Describe measurable outcomes, milestones, and success criteria..."
              modules={{
                toolbar: [
                  ["bold", "italic", "underline"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link"],
                  ["clean"],
                ],
              }}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
          <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" required>
            {GOAL_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Due date *</label>
          <input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" required />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={loading} className="btn-primary px-4 py-2">Create</button>
          <button type="button" onClick={() => navigate("/goals")} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
        </div>
      </form>
    </div>
  );
}
